import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils";

/**
 * Icon Base Component
 *
 * Shared SVG wrapper providing consistent styling and accessibility
 * for all icon components in the design system.
 *
 * Design System: V&A Waterfront Foundation Design System
 * Standard coordinate system (viewBox: 0 0 24 24)
 */

export const iconVariants = cva("shrink-0", {
  variants: {
    size: {
      sm: "size-4",
      // 16px
      md: "size-5",
      // 20px
      lg: "size-6",
      // 24px
      xl: "size-6 desktop:size-8"
    }
  },
  defaultVariants: {
    size: "md"
  }
});
export interface IconBaseProps extends React.SVGProps<SVGSVGElement>, VariantProps<typeof iconVariants> {
  /**
   * If true, the icon is purely decorative and will be hidden from screen readers
   * @default true
   */
  decorative?: boolean;
}
const IconBase = React.forwardRef<SVGSVGElement, IconBaseProps>((t0, ref) => {
  const $ = _c(19);
  let children;
  let className;
  let props;
  let size;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      size,
      decorative: t1,
      className,
      children,
      viewBox: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = size;
    $[5] = t1;
    $[6] = t2;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    size = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const decorative = t1 === undefined ? true : t1;
  const viewBox = t2 === undefined ? "0 0 24 24" : t2;
  let t3;
  if ($[7] !== decorative) {
    t3 = decorative ? {
      "aria-hidden": "true" as const
    } : {
      role: "img" as const
    };
    $[7] = decorative;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const ariaProps = t3;
  let t4;
  if ($[9] !== className || $[10] !== size) {
    t4 = cn(iconVariants({
      size
    }), className);
    $[9] = className;
    $[10] = size;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== ariaProps || $[13] !== children || $[14] !== props || $[15] !== ref || $[16] !== t4 || $[17] !== viewBox) {
    t5 = <svg ref={ref} xmlns="http://www.w3.org/2000/svg" viewBox={viewBox} fill="currentColor" className={t4} {...ariaProps} {...props}>{children}</svg>;
    $[12] = ariaProps;
    $[13] = children;
    $[14] = props;
    $[15] = ref;
    $[16] = t4;
    $[17] = viewBox;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
});
IconBase.displayName = "IconBase";
export { IconBase };